import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, GlassCard, KpiCard } from "@/components/Primitives";
import RangeSelect, { DEFAULT_RANGE } from "@/components/RangeSelect";
import ExportButton from "@/components/ExportButton";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Crown, Repeat, TrendingUp, Briefcase, Users, ChevronRight } from "lucide-react";

const tooltipStyle = {
  contentStyle: {
    background: "rgba(15,15,18,0.94)", border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 14, backdropFilter: "blur(20px)", color: "#fff", fontSize: 12,
  },
  labelStyle: { color: "rgba(255,255,255,0.5)" },
};

export default function Profitability() {
  const [prof, setProf] = useState(null);
  const [mrr, setMrr] = useState(null);
  const [sortBy, setSortBy] = useState("lifetime_revenue");
  const [range, setRange] = useState(DEFAULT_RANGE);

  useEffect(() => {
    (async () => {
      const [p, m] = await Promise.all([api.get("/profitability"), api.get("/mrr")]);
      setProf(p.data); setMrr(m.data);
    })();
  }, []);

  const rows = useMemo(() => {
    if (!prof) return [];
    return [...prof.clients].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
  }, [prof, sortBy]);

  if (!prof || !mrr) return <div className="text-white/40">Loading…</div>;

  const top = rows[0];

  return (
    <div data-testid="profitability-page">
      <PageHeader
        title="Profitability & MRR"
        subtitle="Recurring revenue, client value, and where your margin actually lives."
        testId="profitability-header"
        action={
          <div className="flex items-center gap-2.5">
            <RangeSelect value={range} onChange={setRange} testIdPrefix="prof-range" />
            <ExportButton endpoint="/exports/profitability" range={range} testId="prof-export-btn" label="Export" />
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <KpiCard testId="mrr-total" label="Total MRR" value={fmtMoney(mrr.total_mrr)} icon={Repeat} accent="from-[#007AFF] to-[#0050C8]" hint={`ARR ${fmtMoney(mrr.arr)}`} />
        <KpiCard testId="mrr-hosting" label="Hosting MRR" value={fmtMoney(mrr.hosting_mrr)} icon={Repeat} accent="from-cyan-400 to-cyan-600" hint={`Profit ${fmtMoney(mrr.hosting_profit)}/mo`} />
        <KpiCard testId="mrr-maintenance" label="Maintenance MRR" value={fmtMoney(mrr.maintenance_mrr)} icon={Repeat} accent="from-violet-400 to-violet-600" hint={`${mrr.active_maintenance} active`} />
        <KpiCard testId="mrr-stripe" label="Stripe MRR" value={fmtMoney(mrr.stripe_mrr)} icon={Repeat} accent="from-emerald-400 to-emerald-600" hint={`${mrr.active_subscriptions} subs`} />
      </div>

      <GlassCard className="mb-8" testId="mrr-chart-card">
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Recurring revenue · Last 12 months</div>
          <div className="font-display text-2xl font-light mt-1">Hosting vs. Maintenance</div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={mrr.series}>
            <defs>
              <linearGradient id="host" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#5AC8FA" stopOpacity={0.45} />
                <stop offset="95%" stopColor="#5AC8FA" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="maint" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#BF5AF2" stopOpacity={0.45} />
                <stop offset="95%" stopColor="#BF5AF2" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis dataKey="label" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false}
                   tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
            <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} cursor={{ stroke: "rgba(0,122,255,0.4)", strokeWidth: 1, fill: "transparent" }} />
            <Legend wrapperStyle={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }} />
            <Area type="monotone" dataKey="hosting" name="Hosting" stroke="#5AC8FA" strokeWidth={2} fill="url(#host)" />
            <Area type="monotone" dataKey="maintenance" name="Maintenance" stroke="#BF5AF2" strokeWidth={2} fill="url(#maint)" />
          </AreaChart>
        </ResponsiveContainer>
      </GlassCard>

      {top && (
        <GlassCard className="mb-8 !bg-gradient-to-br from-[#007AFF]/[0.08] to-transparent" testId="top-client">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80">Top Client</div>
              <div className="font-display text-2xl text-white mt-1">{top.company_name}</div>
              <div className="text-xs text-white/55 mt-1">
                Lifetime {fmtMoney(top.lifetime_revenue)} · MRR {fmtMoney(top.total_mrr)} · {top.active_projects} active project{top.active_projects === 1 ? "" : "s"}
              </div>
            </div>
          </div>
        </GlassCard>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-xl">Client profitability</div>
        <div className="flex items-center gap-2 text-[11px] text-white/45">
          <span>Sort by:</span>
          {[
            ["lifetime_revenue", "Lifetime"],
            ["total_mrr", "MRR"],
            ["annual_recurring", "ARR"],
            ["active_projects", "Active"],
          ].map(([k, label]) => (
            <button
              key={k} onClick={() => setSortBy(k)} data-testid={`sort-${k}`}
              className={`px-2.5 py-1 rounded-full ${sortBy === k ? "bg-[#007AFF] text-white" : "bg-white/[0.04] hover:bg-white/[0.08]"}`}
            >{label}</button>
          ))}
        </div>
      </div>

      <GlassCard>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                <th className="py-3 pr-4">#</th>
                <th className="py-3 pr-4">Client</th>
                <th className="py-3 pr-4 text-right">Lifetime Revenue</th>
                <th className="py-3 pr-4 text-right">Hosting MRR</th>
                <th className="py-3 pr-4 text-right">Maint. MRR</th>
                <th className="py-3 pr-4 text-right">Total MRR</th>
                <th className="py-3 pr-4 text-right">ARR</th>
                <th className="py-3 pr-4 text-right">Projects</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {rows.map((r, i) => (
                <tr key={r.client_id} className="hover:bg-white/[0.03]" data-testid={`profit-row-${r.client_id}`}>
                  <td className="py-3 pr-4 text-white/40 font-mono text-xs">{i + 1}</td>
                  <td className="py-3 pr-4 text-white">{r.company_name}</td>
                  <td className="py-3 pr-4 text-right text-emerald-300">{fmtMoney(r.lifetime_revenue)}</td>
                  <td className="py-3 pr-4 text-right text-white/65">{fmtMoney(r.hosting_mrr)}</td>
                  <td className="py-3 pr-4 text-right text-white/65">{fmtMoney(r.maintenance_mrr)}</td>
                  <td className="py-3 pr-4 text-right font-medium">{fmtMoney(r.total_mrr)}</td>
                  <td className="py-3 pr-4 text-right text-white/65">{fmtMoney(r.annual_recurring)}</td>
                  <td className="py-3 pr-4 text-right text-white/65">{r.active_projects}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-white/40">No client data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
}
